import { UserProduct } from '@accedo/one-plugin-sdk';

import {
  PackageDefinition,
  ProfileResponse,
  Subscription,
} from '#/client/models';
import { getPlanInformationById } from '#/plugins/paywall/utils';
import { paywallActions } from '#/store/paywall/paywall.store';
import { ClientPaywallApi } from '#/store/paywall/paywallApi';

export interface NonWebItem {
  coreSubscription: Subscription;
}

interface SubscriptionMapEntry {
  subscription: Subscription;
  name?: string;
}

export interface ProductItem {
  coreSubscription: Subscription;
  userProduct: UserProduct;
}

export const collectProductDetails = (
  profileResponse: ProfileResponse | undefined,
  packageDefinition?: PackageDefinition
) => {
  const subscriptions: Subscription[] =
    profileResponse?.entitlements.subscriptions ?? [];

  const planInfoById = getPlanInformationById(
    packageDefinition,
    paywallActions.transformProductIdentifierSafely
  );

  const userProductIds: string[] = [];
  const subscriptionMap: Record<string, SubscriptionMapEntry> = {};
  const nonWebItems: NonWebItem[] = [];

  subscriptions.forEach((subscription) => {
    const productId =
      subscription.creditCardProductDetails?.productId ??
      subscription.paypalProductDetails?.productId;

    const filteredProductId =
      paywallActions.transformProductIdentifierSafely(productId);

    if (filteredProductId) {
      userProductIds.push(filteredProductId);
      subscriptionMap[filteredProductId] = {
        subscription,
        name: planInfoById?.[filteredProductId]?.name,
      };
    } else {
      nonWebItems.push({ coreSubscription: subscription });
    }
  });

  return { userProductIds, subscriptionMap, nonWebItems };
};

export const fetchAndMapUserProducts = async (
  userProductIds: string[],
  getUserProducts: ClientPaywallApi['getUserProducts'],
  subscriptionMap: Record<string, SubscriptionMapEntry>
) => {
  return ((await getUserProducts?.(userProductIds)) ?? []).map(
    (userProduct): ProductItem => {
      const entry = subscriptionMap[userProduct.productIdentifier];

      return {
        coreSubscription: entry?.subscription,
        userProduct: {
          ...userProduct,
          // use the subscription name from the package definition. Otherwise, we fallback to the title from the user product API response.
          title: entry?.name ?? userProduct.title,
        },
      };
    }
  );
};
